import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '@budget/core';
import type { IncomeByJobData, JobRecord } from '@budget/core';
import { CardHeader } from './CardHeader';
import { getPersonName } from '../config/personNames';
import type { Scope } from './ScopeToggle';
import { useMediaQuery } from '../hooks/useMediaQuery';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// Convert "YYYY-MM" → total months since year 0. Used as the chart's numeric
// unit so we can place bars by absolute calendar position. Month-level
// precision matches the sheet's resolution; the chart never needs day-level.
function ymToMonths(ym: string): number {
  const [y, m] = ym.split('-').map(Number);
  return y * 12 + (m - 1);
}

function monthsToLabel(months: number): string {
  const y = Math.floor(months / 12);
  const m = months - y * 12;
  return `${MONTH_LABELS[m]} ${y}`;
}

interface PaySegment {
  startMonth: string;
  endMonth: string | null;
  monthlySalary: number;
  annualSalary: number;
  isCurrent: boolean;
}

interface GanttRow {
  // Stable React key — disambiguates jobs at the same company/title.
  key: string;
  person: 'dakota' | 'natalia';
  title: string;
  company: string;
  // Combined span across all pay segments.
  startMonth: string;
  endMonth: string | null;
  isCurrent: boolean;
  // Months from year 0. `offset` = earliest start; `duration` = latest end -
  // earliest start. For current rows, end is always `nowMonths` (set in
  // buildRows) so every "Present" arrow lines up at the same horizontal
  // anchor.
  offset: number;
  duration: number;
  // Pay history, sorted chronologically. A single-segment row renders like
  // a normal Gantt bar; multi-segment rows list each pay tier in the
  // tooltip.
  paySegments: PaySegment[];
}

function buildRows(jobs: IncomeByJobData, scope: Scope, nowMonths: number): GanttRow[] {
  const pick: JobRecord[] = (() => {
    if (scope === 'dakota') return jobs.dakota;
    if (scope === 'natalia') return jobs.natalia;
    return [...jobs.dakota, ...jobs.natalia];
  })();

  // Group jobs that share person + company + title — these represent the
  // same role over time (e.g. a raise creates a new sheet row but the
  // position itself didn't change). Each grouped row collapses to one Gantt
  // bar with a pay timeline in the tooltip.
  const groups = new Map<string, JobRecord[]>();
  for (const j of pick) {
    const key = `${j.person}|${j.company}|${j.title}`;
    const arr = groups.get(key) ?? [];
    arr.push(j);
    groups.set(key, arr);
  }

  const merged: GanttRow[] = [];
  let i = 0;
  for (const [, jobsInGroup] of groups) {
    const sortedSegs = [...jobsInGroup].sort((a, b) =>
      a.startMonth.localeCompare(b.startMonth),
    );
    const first = sortedSegs[0];
    const last = sortedSegs[sortedSegs.length - 1];
    const startMonth = first.startMonth;
    const endMonth = last.endMonth;
    const isCurrent = endMonth === null;
    const start = ymToMonths(startMonth);
    const end = isCurrent ? nowMonths : ymToMonths(endMonth!);
    const duration = isCurrent ? Math.max(0, end - start) : Math.max(1, end - start);
    merged.push({
      key: `${i++}-${first.person}-${first.company}-${first.title}-${startMonth}`,
      person: first.person,
      title: first.title,
      company: first.company,
      startMonth,
      endMonth,
      isCurrent,
      offset: start,
      duration,
      paySegments: sortedSegs.map((j) => ({
        startMonth: j.startMonth,
        endMonth: j.endMonth,
        monthlySalary: j.monthlySalary,
        annualSalary: j.annualSalary,
        isCurrent: j.endMonth === null,
      })),
    });
  }

  // Sort rows by start, with person as a stable tie-break so household view
  // is deterministic across re-renders.
  return merged.sort((a, b) => {
    const cmp = a.startMonth.localeCompare(b.startMonth);
    if (cmp !== 0) return cmp;
    return a.person.localeCompare(b.person);
  });
}

interface TooltipState {
  row: GanttRow;
  x: number;
  y: number;
}

function GanttTooltip({ row, x, y }: TooltipState) {
  const startLabel = monthsToLabel(ymToMonths(row.startMonth));
  const endLabel = row.isCurrent
    ? 'Present'
    : monthsToLabel(ymToMonths(row.endMonth!));
  const years = Math.floor(row.duration / 12);
  const months = row.duration - years * 12;
  const durationText =
    [years > 0 ? `${years}y` : null, months > 0 ? `${months}mo` : null]
      .filter(Boolean)
      .join(' ') || '<1mo';
  const multiSegment = row.paySegments.length > 1;
  const onlySegment = row.paySegments[0];
  return (
    <div
      className="jobs-tooltip"
      style={{ left: x + 14, top: y + 14 }}
      role="tooltip"
    >
      <div
        style={{
          fontWeight: 600,
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span className={`person-dot dot-${row.person}`} />
        <span>{row.title || row.company}</span>
      </div>
      {row.title && row.company && (
        <div style={{ color: 'var(--berg-text-secondary)', marginBottom: 6 }}>
          {row.company}
        </div>
      )}
      <div style={{ marginBottom: multiSegment ? 6 : 2 }}>
        <span style={{ color: 'var(--berg-text-tertiary)' }}>Dates:</span>{' '}
        {startLabel} – {endLabel}{' '}
        <span style={{ color: 'var(--berg-text-tertiary)' }}>({durationText})</span>
      </div>
      {multiSegment ? (
        <div>
          <div
            style={{
              color: 'var(--berg-text-tertiary)',
              fontSize: '0.6875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              marginBottom: 4,
            }}
          >
            Pay history
          </div>
          {row.paySegments.map((seg, idx) => {
            const segStart = monthsToLabel(ymToMonths(seg.startMonth));
            const segEnd = seg.isCurrent
              ? 'Present'
              : monthsToLabel(ymToMonths(seg.endMonth!));
            return (
              <div
                key={`${seg.startMonth}-${idx}`}
                style={{
                  marginBottom: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span style={{ color: 'var(--berg-text-tertiary)' }}>
                  {segStart} – {segEnd}
                </span>
                <span className="tabular">
                  {seg.annualSalary > 0
                    ? `${formatCurrency(seg.annualSalary, 'USD', 'en-US', 0)}/yr`
                    : seg.monthlySalary > 0
                      ? `${formatCurrency(seg.monthlySalary, 'USD', 'en-US', 0)}/mo`
                      : '—'}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {onlySegment.monthlySalary > 0 && (
            <div style={{ marginBottom: 2 }}>
              <span style={{ color: 'var(--berg-text-tertiary)' }}>Monthly:</span>{' '}
              {formatCurrency(onlySegment.monthlySalary, 'USD', 'en-US', 0)}
            </div>
          )}
          {onlySegment.annualSalary > 0 && (
            <div>
              <span style={{ color: 'var(--berg-text-tertiary)' }}>Annual:</span>{' '}
              {formatCurrency(onlySegment.annualSalary, 'USD', 'en-US', 0)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface JobsGanttChartProps {
  jobs: IncomeByJobData;
  scope: Scope;
}

export function JobsGanttChart({ jobs, scope }: JobsGanttChartProps) {
  const now = new Date();
  const nowMonths = now.getFullYear() * 12 + now.getMonth();

  const rows = useMemo(
    () => buildRows(jobs, scope, nowMonths),
    [jobs, scope, nowMonths],
  );

  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  // Skip tooltips on touch devices — tapping a bar leaves a sticky tooltip
  // pinned to the row that the user can't dismiss without leaving the page.
  const hasHover = useMediaQuery('(hover: hover)');
  const isMobile = useMediaQuery('(max-width: 767.98px)');

  // Declared BEFORE the early return below so these hooks run in the same order
  // every render (react-hooks/rules-of-hooks). The label column auto-sizes to
  // the widest label: measure each row's natural width (by temporarily clearing
  // the inline-controlled width) and write the max back to `--jobs-label-width`,
  // which the row labels, gridlines, and x-axis all key off of. The effect
  // no-ops when the gantt isn't rendered (its container ref is null then).
  const showPersonChip = scope === 'household';
  const ganttRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const container = ganttRef.current;
    if (!container) return;
    const labels = Array.from(
      container.querySelectorAll<HTMLElement>('.jobs-row-label'),
    );
    if (labels.length === 0) return;
    labels.forEach((el) => {
      el.style.width = 'auto';
    });
    const widths = labels.map((el) => el.getBoundingClientRect().width);
    labels.forEach((el) => {
      el.style.width = '';
    });
    const maxWidth = Math.max(...widths);
    container.style.setProperty('--jobs-label-width', `${Math.ceil(maxWidth)}px`);
  }, [rows, showPersonChip]);

  const scopeLabel =
    scope === 'household'
      ? 'Dakota & Natalia'
      : getPersonName(scope as 'dakota' | 'natalia');

  if (rows.length === 0) {
    return (
      <div className="card">
        <CardHeader title="Jobs over time" subtitle={`${scopeLabel}'s employment history`} />
        <div className="empty-state">No jobs found in the sheet for this scope.</div>
      </div>
    );
  }

  const minMonth = Math.min(...rows.map((r) => r.offset));
  const maxMonth = Math.max(...rows.map((r) => r.offset + r.duration));
  // Pad the domain to whole years so the X-axis ticks line up cleanly with
  // calendar year boundaries. Anchor on Jan of the earliest year, end on Jan
  // of the year after the latest activity (giving "Present" jobs a little
  // breathing room past today).
  const anchorYear = Math.floor(minMonth / 12);
  const endYear = Math.floor(maxMonth / 12) + 1;
  const domainStart = anchorYear * 12;
  const domainEnd = endYear * 12;
  const domainSpan = domainEnd - domainStart;

  const years: number[] = [];
  for (let y = anchorYear; y <= endYear; y++) years.push(y);

  // Thin the x-axis labels on mobile so they don't overlap. Pick a "nice"
  // stride from {1,2,5,10,20} that brings the label count under ~5 — keeps
  // ticks on round years (2010, 2015, 2020…) instead of arbitrary intervals.
  // Gridlines stay at every year regardless; only the year-label row thins.
  const labelStride = (() => {
    if (!isMobile) return 1;
    for (const s of [1, 2, 5, 10, 20]) {
      if (years.length / s <= 5) return s;
    }
    return 25;
  })();
  const xAxisYears = years.filter((y) => y % labelStride === 0);

  // Each row's bar is positioned with `right` + `width` (instead of `left` +
  // `width`) so the bar's right edge always anchors at end-of-job. That
  // matters for "Present" jobs whose duration collapses to 0 — `min-width`
  // on the bar forces a visible stub anchored at "now" rather than nudging
  // its end past "now".
  function rowGeometry(row: GanttRow) {
    const startPct = ((row.offset - domainStart) / domainSpan) * 100;
    const endPct = ((row.offset + row.duration - domainStart) / domainSpan) * 100;
    const widthPct = Math.max(0, endPct - startPct);
    const rightPct = 100 - endPct;
    return { rightPct, widthPct };
  }

  return (
    <div className="card">
      <CardHeader
        title="Jobs over time"
        subtitle={`${scopeLabel}'s employment history`}
      />

      <div className="jobs-gantt" ref={ganttRef}>
        {/* Gridlines drawn once for the whole chart, behind the rows. Skip
            the leftmost tick (chart-left = anchor year) to avoid drawing a
            line right at the label column boundary, and the rightmost (which
            would render at exactly 100% and effectively be invisible). */}
        <div className="jobs-gantt-gridlines">
          {years.slice(1, -1).map((y) => {
            const left = ((y * 12 - domainStart) / domainSpan) * 100;
            return (
              <div
                key={y}
                className="jobs-gantt-gridline"
                style={{ left: `${left}%` }}
              />
            );
          })}
        </div>

        {rows.map((row) => {
          const { rightPct, widthPct } = rowGeometry(row);
          const primary = row.title || row.company;
          const secondary = row.title && row.company ? row.company : '';
          return (
            <div
              key={row.key}
              className="jobs-row"
              onMouseEnter={
                hasHover
                  ? (e) => setTooltip({ row, x: e.clientX, y: e.clientY })
                  : undefined
              }
              onMouseMove={
                hasHover
                  ? (e) =>
                      setTooltip((prev) =>
                        prev && prev.row.key === row.key
                          ? { ...prev, x: e.clientX, y: e.clientY }
                          : { row, x: e.clientX, y: e.clientY },
                      )
                  : undefined
              }
              onMouseLeave={hasHover ? () => setTooltip(null) : undefined}
            >
              <div className="jobs-row-label">
                <div className="jobs-row-label-text">
                  <span className="jobs-row-title">{primary}</span>
                  {secondary && (
                    <span className="jobs-row-company">{secondary}</span>
                  )}
                </div>
                {showPersonChip && (
                  <span className={`person-dot dot-${row.person}`} />
                )}
              </div>
              <div className="jobs-row-track">
                <div
                  className={clsx(
                    'jobs-row-bar',
                    `jobs-row-bar-${row.person}`,
                    row.isCurrent && 'jobs-row-bar-current',
                  )}
                  style={{ right: `${rightPct}%`, width: `${widthPct}%` }}
                >
                  <div className="jobs-row-bar-head" />
                  {row.isCurrent && <div className="jobs-row-bar-arrow" />}
                </div>
              </div>
            </div>
          );
        })}

        <div className="jobs-gantt-xaxis">
          {xAxisYears.map((y) => {
            const left = ((y * 12 - domainStart) / domainSpan) * 100;
            return (
              <div
                key={y}
                className="jobs-gantt-xaxis-year"
                style={{ left: `${left}%` }}
              >
                {y}
              </div>
            );
          })}
        </div>
      </div>

      {tooltip && <GanttTooltip {...tooltip} />}
    </div>
  );
}
